import React from 'react';
import { LayoutDashboard, Package, Tag, BarChart2, Lightbulb, Settings, LogOut, Zap, ChevronRight } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'catalog', label: 'Product Catalog', icon: Package },
  { id: 'pricing', label: 'Pricing Management', icon: Tag },
  { id: 'analytics', label: 'Scan Analytics', icon: BarChart2 },
  { id: 'recommendations', label: 'Recommendations', icon: Lightbulb },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const TIER_STYLES = {
  basic: { color: '#94A3B8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.2)' },
  silver: { color: '#CBD5E1', bg: 'rgba(203,213,225,0.1)', border: 'rgba(203,213,225,0.2)' },
  gold: { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.2)' },
  platinum: { color: '#00E5FF', bg: 'rgba(0,229,255,0.1)', border: 'rgba(0,229,255,0.2)' },
};

export default function PartnerSidebar({ activeSection, onSectionChange, partnerProfile, onSignOut }) {
  const tier = partnerProfile?.partner_tier || 'basic';
  const tierStyle = TIER_STYLES?.[tier] || TIER_STYLES?.basic;

  return (
    <aside className="w-64 min-h-screen flex flex-col" style={{ background: 'rgba(10,20,35,0.98)', borderRight: '1px solid rgba(0,229,255,0.1)', backdropFilter: 'blur(20px)' }}>
      {/* Logo */}
      <div className="px-6 py-5" style={{ borderBottom: '1px solid rgba(0,229,255,0.08)' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.2), rgba(123,47,190,0.2))', border: '1px solid rgba(0,229,255,0.3)' }}>
            <Zap size={18} style={{ color: '#00E5FF' }} />
          </div>
          <div>
            <p className="text-white font-bold text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>FinVisAR</p>
            <p className="text-xs" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>Partner Portal</p>
          </div>
        </div>
      </div>

      {/* Partner Info */}
      <div className="px-4 py-4" style={{ borderBottom: '1px solid rgba(0,229,255,0.06)' }}>
        <div className="rounded-xl p-3" style={{ background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.08)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg, #7B2FBE, #00E5FF)' }}>
              {(partnerProfile?.business_name || 'P')?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold truncate" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {partnerProfile?.business_name || 'Partner'}
              </p>
              <span
                className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full capitalize mt-0.5"
                style={{ color: tierStyle?.color, background: tierStyle?.bg, border: `1px solid ${tierStyle?.border}`, fontFamily: 'DM Sans, sans-serif' }}
              >
                {tier}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {navItems?.map((item) => {
          const Icon = item?.icon;
          const isActive = activeSection === item?.id;
          return (
            <button
              key={item?.id}
              onClick={() => onSectionChange(item?.id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 group"
              style={{
                background: isActive ? 'linear-gradient(135deg, rgba(0,229,255,0.12), rgba(123,47,190,0.08))' : 'transparent',
                border: isActive ? '1px solid rgba(0,229,255,0.18)' : '1px solid transparent',
              }}
            >
              <Icon
                size={17}
                style={{ color: isActive ? '#00E5FF' : '#475569', transition: 'color 0.2s', flexShrink: 0 }}
              />
              <span
                className="text-sm font-medium flex-1"
                style={{
                  color: isActive ? '#E2E8F0' : '#64748B',
                  fontFamily: 'DM Sans, sans-serif',
                  transition: 'color 0.2s',
                }}
              >
                {item?.label}
              </span>
              {isActive && <ChevronRight size={13} style={{ color: '#00E5FF', flexShrink: 0 }} />}
            </button>
          );
        })}
      </nav>

      {/* Sign Out */}
      <div className="px-3 pb-5" style={{ borderTop: '1px solid rgba(0,229,255,0.06)', paddingTop: '12px' }}>
        <button
          onClick={onSignOut}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group hover:bg-red-500/5"
          style={{ border: '1px solid rgba(239,68,68,0.12)' }}
        >
          <LogOut size={17} style={{ color: '#EF4444' }} />
          <span className="text-sm font-medium" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
